import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, CheckCircle, CheckCircle2, XCircle, ChevronRight, ChevronLeft, FileText, Check } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopNav from '../components/TopNav';
import { useGetExamQuery, useSubmitExamMutation } from '../redux/Clinictrainingapi';

// ── Split questions into pages of 4, remainder spread +1 to first pages ──
// 20 questions → 5 pages of 4       → [4,4,4,4,4]
// 22 questions → 5 pages, rem=2     → [5,5,4,4,4]
// 14 questions → 4 pages, rem=2     → [5,5,4,4]  (wait: floor(14/4)=3 pages of 4=12, rem=2 → 3+1=4 pages? No)
// Rule: pageCount = floor(total/4), remainder = total % 4
//   if remainder === 0 → all pages = 4
//   else → pageCount stays floor(total/4), first `remainder` pages get 5
// 22 → floor(22/4)=5 pages, rem=2 → [5,5,4,4,4] ✓
// 20 → floor(20/4)=5 pages, rem=0 → [4,4,4,4,4] ✓
// 23 → floor(23/4)=5 pages, rem=3 → [5,5,5,4,4] ✓
function buildPages(questions) {
  const total = questions.length;
  if (!total) return [];
  const pageCount = Math.floor(total / 4) || 1;
  const remainder = total % 4;
  const pages = [];
  let cursor = 0;
  for (let i = 0; i < pageCount; i++) {
    const size = 4 + (i < remainder ? 1 : 0);
    pages.push(questions.slice(cursor, cursor + size));
    cursor += size;
  }
  return pages;
}

export default function FinalExam() {
  const navigate = useNavigate();

  const [answers, setAnswers]             = useState({});
  const [revealed, setRevealed]           = useState({});  // locked after first pick
  const [timeRemaining, setTimeRemaining] = useState(1200);
  const [isStarted, setIsStarted]         = useState(false);
  const [isSubmitting, setIsSubmitting]   = useState(false);
  const [currentPage, setCurrentPage]     = useState(0);

  // Scroll to top whenever the page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  const { data: exams, isLoading, isError } = useGetExamQuery();
  const [submitExam] = useSubmitExamMutation();

  const exam = exams?.[0];

  // Stable refs for use inside timer/submit
  const answersRef       = useRef(answers);       answersRef.current       = answers;
  const examRef          = useRef(exam);           examRef.current          = exam;
  const timeRemainingRef = useRef(timeRemaining); timeRemainingRef.current = timeRemaining;

  const handleSubmit = useCallback(async () => {
    const currentExam    = examRef.current;
    const currentAnswers = answersRef.current;
    if (!currentExam || isSubmitting) return;
    setIsSubmitting(true);

    const answersPayload = Object.entries(currentAnswers).map(([questionId, selectedLabel]) => ({
      question_id: Number(questionId),
      answer: selectedLabel,
    }));

    try {
      const result       = await submitExam({ id: currentExam.id, answers: answersPayload }).unwrap();
      const questions    = currentExam.questions ?? [];
      const correctCount = result?.correct_count ?? 0;
      const score        = result?.score ?? Math.round((correctCount / questions.length) * 100);

      navigate('/final-exam/results', {
        state: {
          score,
          totalQuestions: result?.total_questions ?? questions.length,
          correctAnswers: correctCount,
          timeSpent:      1200 - timeRemainingRef.current,
          isPassed:       result?.is_passed ?? score >= 80,
          passingScore:   result?.passing_score ?? 80,
        },
      });
    } catch (err) {
      // console.error('Exam submission failed:', err);
      setIsSubmitting(false);
    }
  }, [navigate, submitExam, isSubmitting]);

  // Countdown timer
  useEffect(() => {
    if (!isStarted) return;
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) { clearInterval(timer); handleSubmit(); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [isStarted, handleSubmit]);

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  // Pick an answer — locked after first selection, shows green/red immediately
  const handleAnswer = (questionId, optionLabel) => {
    if (revealed[questionId]) return;
    setAnswers(prev  => ({ ...prev, [questionId]: optionLabel }));
    setRevealed(prev => ({ ...prev, [questionId]: true }));
  };

  // Radio border — hollow circle; only shows wrong (red) on the user's pick,
  // never reveals which option is correct (no green on correct answer).
  const radioBorder = (q, opt) => {
    const isSelected = answers[q.id] === opt.label;
    const isRevealed = revealed[q.id];
    const userCorrect = answers[q.id] === q.correct_answer;
    if (!isRevealed) return isSelected ? 'border-primary' : 'border-gray-300';
    if (isSelected && userCorrect)   return 'border-emerald-500';  // user got it right
    if (isSelected && !userCorrect)  return 'border-red-400';      // user got it wrong
    return 'border-gray-300';                                      // other options stay neutral
  };

  // Dot inside radio — only rendered on the selected option
  const radioDotColor = (q, opt) => {
    const isRevealed = revealed[q.id];
    const userCorrect = answers[q.id] === q.correct_answer;
    if (!isRevealed) return 'bg-primary';
    if (userCorrect) return 'bg-emerald-500';
    return 'bg-red-400';
  };

  // ── Guards ──────────────────────────────────────────────────────────────
  if (isLoading) return (
    <div className="min-h-screen bg-surface flex items-center justify-center">
      <p className="text-gray-400 text-sm">Loading exam...</p>
    </div>
  );
  if (isError || !exam) return (
    <div className="min-h-screen bg-surface flex items-center justify-center">
      <p className="text-red-400 text-sm">Failed to load exam. Please try again.</p>
    </div>
  );

  const questions      = (exam.questions ?? []).filter(q => q.question?.trim());
  const pages          = buildPages(questions);
  const totalPages     = pages.length;
  const totalQuestions = questions.length;
  const answeredCount  = Object.keys(answers).length;

  const pageQuestions = pages[currentPage] ?? [];
  const pageComplete  = pageQuestions.length > 0 && pageQuestions.every(q => revealed[q.id]);
  const isLastPage    = currentPage === totalPages - 1;

  // ── Landing screen ──────────────────────────────────────────────────────
  if (!isStarted) {
    return (
      <div className="min-h-screen bg-surface">
        <Sidebar />
        <div className="ml-0 md:ml-16">
          <TopNav
            backTo="/dashboard"
            backLabel="BACK TO CURRICULUM"
            breadcrumbs={['COMPLIANCE TRAINING', 'FINAL EXAM']}
            rightContent={
              <span className="flex items-center gap-1.5 text-xs text-primary font-medium">
                <CheckCircle size={14} /> HIPAA Certification
              </span>
            }
          />
          <div className="flex justify-center px-4 sm:px-6 py-6 sm:py-8">
            <motion.div
              className="w-full max-w-2xl bg-white rounded-2xl border border-gray-200 p-6 sm:p-10"
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            >
              <div className="text-xs font-bold text-primary tracking-widest uppercase mb-3">FINAL CERTIFICATION</div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">{exam.title ?? 'Final Exam'}</h1>
              <p className="text-gray-500 leading-relaxed mb-8">
                Please ensure you are in a quiet environment before starting. This exam will certify your compliance knowledge for the current fiscal year.
              </p>

              <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-8">
                <div className="text-center p-4 bg-gray-50 rounded-xl">
                  <Clock size={20} className="text-primary mx-auto mb-2" />
                  <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Time Limit</div>
                  <div className="text-lg font-bold text-gray-900">20 min</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-xl">
                  <CheckCircle size={20} className="text-primary mx-auto mb-2" />
                  <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Passing Score</div>
                  <div className="text-lg font-bold text-gray-900">{exam.passing_score ?? 80}%</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-xl">
                  <FileText size={20} className="text-primary mx-auto mb-2" />
                  <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Questions</div>
                  <div className="text-lg font-bold text-gray-900">{totalQuestions}</div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-5 mb-8">
                <h3 className="font-semibold text-gray-900 mb-2">Instructions</h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  The exam is split across {totalPages} pages (~4 questions each). Answer every question on a page to unlock the next. You have three attempts to pass.
                </p>
              </div>

              <div className="text-center">
                <button
                  onClick={() => setIsStarted(true)}
                  className="flex items-center gap-2.5 bg-gradient-to-r from-primary to-cyan-400 hover:from-primary-700 hover:to-primary text-white px-8 sm:px-10 py-3.5 rounded-full font-semibold text-sm transition-all cursor-pointer shadow-lg shadow-primary/25 mx-auto"
                >
                  Start Final Exam <ChevronRight size={18} />
                </button>
                <p className="text-xs text-gray-400 mt-4">By clicking start, you agree to the NexaLife Honor Code.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  // ── Exam in progress ────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-surface">
      <Sidebar />
      <div className="ml-0 md:ml-16">
        <TopNav
          backTo="/dashboard"
          backLabel="BACK TO CURRICULUM"
          breadcrumbs={['FINAL CERTIFICATION EXAM']}
          rightContent={
            <span className="flex items-center gap-1.5 text-xs text-primary font-medium">
              <Clock size={14} /> {formatTime(timeRemaining)}
            </span>
          }
        />

        <div className="max-w-xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          {/* Header */}
          <motion.div className="mb-8" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <div className="text-xs font-bold text-primary tracking-widest uppercase mb-2">
              HIPAA Compliance Certification
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Final Certification Exam</h1>
            <p className="text-sm text-gray-400">
              Page {currentPage + 1} of {totalPages} — answer all questions to continue.
            </p>
          </motion.div>

          {/* Questions for this page only — slides in/out on page change */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentPage}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.22 }}
              className="space-y-8"
            >
              {pageQuestions.map((q) => {
                const validOptions = (q.options ?? []).filter(opt => opt.text?.trim());
                const globalIdx    = questions.findIndex(gq => gq.id === q.id);

                const isRevealed = revealed[q.id];
                const userAnswer = answers[q.id];
                const isCorrect  = userAnswer === q.correct_answer;

                return (
                  <div key={q.id} className="bg-white rounded-xl border border-gray-100 p-4 sm:p-6">
                    {/* Question label + Correct/Incorrect badge */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs font-bold text-primary tracking-widest uppercase">
                        Question {q.order ?? globalIdx + 1}
                      </div>
                      <AnimatePresence>
                        {isRevealed && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                            className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${
                              isCorrect ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-500'
                            }`}
                          >
                            {isCorrect ? (
                              <><CheckCircle2 size={13} /> Correct</>
                            ) : (
                              <><XCircle size={13} /> Incorrect</>
                            )}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    <h3 className="text-base font-semibold text-gray-900 mb-4">{q.question}</h3>

                    <div className="space-y-2.5">
                      {validOptions.map((opt) => {
                        const isSelected = answers[q.id] === opt.label;

                        return (
                          <motion.button
                            key={opt.label}
                            onClick={() => handleAnswer(q.id, opt.label)}
                            disabled={isRevealed}
                            whileTap={!isRevealed ? { scale: 0.985 } : {}}
                            className={`w-full text-left px-4 py-3.5 rounded-xl border-2 border-gray-100 bg-white transition-all flex items-center gap-3 ${
                              isRevealed ? 'cursor-default' : 'hover:border-gray-200 cursor-pointer'
                            }`}
                          >
                            {/* Hollow circle — border + dot color gives feedback */}
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${radioBorder(q, opt)}`}>
                              {isSelected && (
                                <motion.div
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  className={`w-2.5 h-2.5 rounded-full ${radioDotColor(q, opt)}`}
                                />
                              )}
                            </div>
                            <span className="text-xs font-bold text-gray-400 w-5 shrink-0">{opt.label}</span>
                            <span className={`text-sm ${isSelected ? 'text-primary font-medium' : 'text-gray-700'}`}>
                              {opt.text}
                            </span>
                          </motion.button>
                        );
                      })}
                    </div>

                    {/* Wrong-answer nudge — does NOT reveal the correct answer */}
                    <AnimatePresence>
                      {isRevealed && !isCorrect && (
                        <motion.div
                          initial={{ opacity: 0, height: 0, marginTop: 0 }}
                          animate={{ opacity: 1, height: 'auto', marginTop: 12 }}
                          exit={{ opacity: 0, height: 0, marginTop: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="text-xs text-red-500 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            That answer is incorrect.
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </motion.div>
          </AnimatePresence>

          {/* Sticky bottom bar */}
          <div className="sticky bottom-0 bg-surface pt-3 pb-5 mt-6">
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">

              {/* Progress — one pill per page */}
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  {pages.map((pg, i) => {
                    const pgDone    = pg.filter(q => revealed[q.id]).length;
                    const isFull    = pgDone === pg.length;
                    const isPartial = pgDone > 0 && !isFull;
                    const isCurrent = i === currentPage;
                    return (
                      <div key={i} className={`h-1.5 rounded-full transition-all ${
                        isFull    ? 'w-5 bg-primary'    :
                        isPartial ? 'w-5 bg-primary/40' :
                        isCurrent ? 'w-5 bg-gray-300'   :
                                    'w-1.5 bg-gray-200'
                      }`} />
                    );
                  })}
                </div>
                <span className="text-xs text-gray-400 font-medium tracking-wider">
                  {answeredCount}/{totalQuestions}
                </span>
              </div>

              {/* Navigation */}
              <div className="flex items-center gap-2 justify-end">
                {currentPage > 0 && (
                  <button
                    onClick={() => setCurrentPage(p => p - 1)}
                    className="flex items-center gap-1.5 border border-gray-200 text-gray-500 px-4 py-3 sm:py-2.5 rounded-full font-semibold text-xs uppercase tracking-wider hover:bg-gray-50 transition-all"
                  >
                    <ChevronLeft size={14} /> Back
                  </button>
                )}

                {isLastPage ? (
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="flex items-center gap-2 bg-gradient-to-r from-primary to-cyan-400 hover:from-primary-700 hover:to-primary text-white px-6 py-3 sm:py-2.5 rounded-full font-semibold text-xs uppercase tracking-wider transition-all shadow-lg shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Submitting…' : 'Submit Exam'}
                    <Check size={14} strokeWidth={3} />
                  </button>
                ) : (
                  <button
                    onClick={() => setCurrentPage(p => p + 1)}
                    disabled={!pageComplete}
                    className="flex items-center gap-2 bg-gradient-to-r from-primary to-cyan-400 hover:from-primary-700 hover:to-primary text-white px-6 py-3 sm:py-2.5 rounded-full font-semibold text-xs uppercase tracking-wider transition-all shadow-lg shadow-primary/20 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                  >
                    Next Page <ChevronRight size={14} />
                  </button>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}